import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Button from '../../components/ui/button';

/**
 * The guest signup wall dialog (anonymous access v1 §8).
 *
 * Purpose-built rather than reusing ConfirmationDialog: a conversion prompt
 * needs action HIERARCHY — one primary action (create account), a quiet text
 * link for existing members, and an unceremonious dismiss — where the shared
 * confirm dialog renders equal-weight buttons. Overlay mechanics (portal,
 * Escape/backdrop close, focus on mount, scroll lock, z 3000) match the
 * shared dialogs per docs/codebase/ui-conventions.md.
 */
interface GuestWallDialogProps {
  message: string;
  onSignup: () => void;
  onLogin: () => void;
  onDismiss: () => void;
}

const GuestWallDialog: React.FC<GuestWallDialogProps> = ({ message, onSignup, onLogin, onDismiss }) => {
  const primaryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    primaryRef.current?.focus();
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onDismiss();
    else if (e.key === 'Enter') onSignup();
  };

  const dialogContent = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 3000, // above sheets (2500) like the other confirm dialogs
      }}
      onClick={onDismiss}
    >
      <div
        role="dialog"
        aria-labelledby="guest-wall-title"
        aria-describedby="guest-wall-message"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        style={{
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 18px 48px rgba(22, 36, 29, 0.22)',
          width: '100%',
          maxWidth: 360,
          padding: '26px 24px 18px',
          textAlign: 'center',
        }}
      >
        <div aria-hidden="true" style={{ fontSize: 26, lineHeight: 1, marginBottom: 10 }}>🔒</div>
        <h2 id="guest-wall-title" style={{ fontSize: 17, fontWeight: 700, color: '#16241d', margin: '0 0 8px' }}>
          Create a free account
        </h2>
        <p id="guest-wall-message" style={{ fontSize: 14, lineHeight: 1.55, color: '#6c7872', margin: '0 0 18px' }}>
          {message}
        </p>
        <Button ref={primaryRef} variant="primary" className="w-full" onClick={onSignup}>
          Create free account
        </Button>
        <p style={{ fontSize: 13.5, color: '#6c7872', margin: '14px 0 0' }}>
          Already have an account?{' '}
          <button
            type="button"
            onClick={onLogin}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              fontWeight: 600,
              color: '#157a55',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Log in
          </button>
        </p>
        <button
          type="button"
          onClick={onDismiss}
          style={{
            background: 'none',
            border: 'none',
            padding: '10px 8px 2px',
            font: 'inherit',
            fontSize: 13,
            color: '#9aa5a0',
            cursor: 'pointer',
          }}
        >
          Not now
        </button>
      </div>
    </div>
  );

  return createPortal(dialogContent, document.body);
};

export default GuestWallDialog;
